import type { PayloadAction } from '@reduxjs/toolkit';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

type IConfirmationDialog<T> =
    | {
          actionQuery: (variables: T) => PayloadAction;
          variables: T | null;
          onBeforeActionQuery?: () => void;
          actionFn?: never;
          setIsSubmitted?: React.Dispatch<React.SetStateAction<boolean>>;
      }
    | {
          actionFn: () => void;
          actionQuery?: never;
          variables?: never;
          onBeforeActionQuery?: never;
          setIsSubmitted?: React.Dispatch<React.SetStateAction<boolean>>;
      };

export const useMethodsConfirmationDialog = <TQueryVariables = any,>({
    actionQuery,
    variables,
    onBeforeActionQuery,
    actionFn,
    setIsSubmitted,
}: IConfirmationDialog<TQueryVariables>) => {
    const dispatch = useDispatch();

    const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] = useState<boolean>(false);

    const handleOpenConfirmationDialog = useCallback(() => setIsOpenConfirmationDialog(true), []);
    const handleCloseConfirmationDialog = useCallback(() => setIsOpenConfirmationDialog(false), []);

    const handleActionQuery = useCallback(() => {
        if (variables && actionQuery) {
            onBeforeActionQuery?.();
            dispatch(actionQuery(variables));
        }

        if (setIsSubmitted) {
            setIsSubmitted(true);
        }

        if (actionFn) {
            actionFn();
        }
    }, [actionFn, actionQuery, dispatch, onBeforeActionQuery, setIsSubmitted, variables]);

    return { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery };
};
